import { useState, useEffect } from 'react';

// Declare global window.CelloAttribution if not already globally typed
declare global {
  interface Window {
    CelloAttribution?: (action: 'getUcc') => Promise<string | null>;
  }
}

interface UseCelloDataReturn {
  data: string | null; // This will hold the UCC string
  isLoading: boolean;
  error: Error | null;
}

export const useCelloData = (): UseCelloDataReturn => {
  const [ucc, setUcc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchCelloUcc = async () => {
      setIsLoading(true);
      setError(null);
      if (window.CelloAttribution && typeof window.CelloAttribution === 'function') {
        try {
          const result = await window.CelloAttribution('getUcc');
          if (result && typeof result === 'string' && result.trim() !== '') {
            setUcc(result.trim());
          } else {
            setUcc(null); // No valid UCC found
          }
        } catch (e) {
          console.error('[useCelloData] Error calling CelloAttribution:', e);
          setError(e instanceof Error ? e : new Error('Error fetching Cello UCC'));
          setUcc(null);
        }
      } else {
        // CelloAttribution not on window, simply means no UCC can be auto-retrieved.
        // This isn't an "error" for the hook itself, just a state.
        setUcc(null);
      }
      setIsLoading(false);
    };

    fetchCelloUcc();
  }, []); // Effect runs once on mount

  return { data: ucc, isLoading, error };
};
